import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { CookiePrefModal } from '@/components/shared/CookiePrefModal';
import {
  acceptAll,
  rejectOptional,
  useCookieConsent,
} from '@/hooks/useCookieConsent';
import { ROUTES } from '@/lib/routes';

/**
 * LGPD/GDPR cookie consent.
 *
 * - Banner at the bottom while consent is `null`.
 * - 'Personalizar' opens a tabbed modal (see CookiePrefModal).
 * - Persists the choice via localStorage (useCookieConsent).
 */
export function CookieConsent() {
  const prefs = useCookieConsent();
  const [open, setOpen] = useState(false);

  const decided = prefs !== null;

  return (
    <>
      <AnimatePresence>
        {!decided && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            role="region"
            aria-label="Aviso de cookies"
            className="fixed inset-x-0 bottom-0 z-40 p-4 md:p-6"
          >
            <div className="container-page max-w-5xl rounded-card-lg bg-white shadow-2xl border border-brand-lightgray/60 p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div className="flex-1 space-y-2">
                  <Badge tone="yellow" size="md">LGPD</Badge>
                  <h2 className="text-lg font-semibold text-brand-purple">
                    Este site usa cookies
                  </h2>
                  <p className="text-sm text-brand-purple/80 leading-relaxed max-w-2xl">
                    Utilizamos cookies essenciais para o funcionamento do site e, com
                    seu consentimento, cookies opcionais para analisar o uso e melhorar
                    sua experiência. Saiba mais em{' '}
                    <Link to={ROUTES.termosDeUso} className="underline hover:text-brand-yellow-text">
                      Termos de Uso
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <Button
                    variant="ghost-purple"
                    size="md"
                    onClick={() => setOpen(true)}
                  >
                    Personalizar
                  </Button>
                  <Button
                    variant="ghost-purple"
                    size="md"
                    onClick={() => rejectOptional()}
                  >
                    Rejeitar opcionais
                  </Button>
                  <Button
                    variant="primary-yellow"
                    size="md"
                    onClick={() => acceptAll()}
                  >
                    Aceitar todos
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookiePrefModal
        open={open}
        onClose={() => setOpen(false)}
        prefs={prefs}
      />
    </>
  );
}
